import React from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';

interface BelagomProfilePhotoProps {
  /** URL van de profielfoto */
  src?: string | null;
  /** Totale afmeting van het frame in px (default 80) */
  size?: number;
  alt?: string;
  /** Optionele online-indicator */
  isOnline?: boolean;
  className?: string;
}

/**
 * Profielfoto met het Belagom spinning logo als frame.
 * De foto staat gecentreerd in het open midden van het logo.
 */
const BelagomProfilePhoto: React.FC<BelagomProfilePhotoProps> = ({
  src,
  size = 80,
  alt,
  isOnline,
  className,
}) => {
  const { t } = useTranslation('common');
  const resolvedAlt = alt ?? t('common.profilePhoto');
  // Binnenste foto is ~62% van het totale frame
  const innerSize = Math.round(size * 0.62);
  const iconSize = Math.round(innerSize * 0.45);

  const normalizedSrc = src && !/^(https?:|\/|data:|blob:)/i.test(src)
    ? `/${src}`
    : src;

  return (
    <div
      className={className}
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      {/* Spinning logo frame — originele blauw/geel kleuren */}
      <img
        src="/belagom-symbol.png"
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: size,
          height: size,
          pointerEvents: 'none',
        }}
      />

      {/* Profielfoto gecentreerd in het logo */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: innerSize,
          height: innerSize,
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: '#f3f4f6',
          border: '2px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {normalizedSrc ? (
          <img
            src={normalizedSrc}
            alt={resolvedAlt}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <User size={iconSize} color="#9ca3af" />
        )}
      </div>

      {/* Online-indicator */}
      {isOnline !== undefined && (
        <span
          style={{
            position: 'absolute',
            bottom: Math.round(size * 0.05),
            right: Math.round(size * 0.05),
            width: Math.max(8, Math.round(size * 0.18)),
            height: Math.max(8, Math.round(size * 0.18)),
            borderRadius: '50%',
            backgroundColor: isOnline ? '#22c55e' : '#d1d5db',
            border: '2px solid #fff',
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
};

export default BelagomProfilePhoto;
